import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Collapse,
  Pagination,
  MenuItem,
  FormControl,
  Select,
  Grid,
  Skeleton,
  CircularProgress,
  Chip,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Autocomplete from "@mui/material/Autocomplete";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import PrintIcon from "@mui/icons-material/Print";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ErrorPage from "../Components/ErrorPage";
import SaleAdd from "./SaleAdd";
import SaleEdit from "./SaleEdit";
import useDelete from "../../api/useDelete";
import useFetchObjects from "../../api/useFetchObjects";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import { printSale } from "../utils/printSale";

const ALL_CUSTOMERS_OPTION = { id: "", name: "All Customer" };

// Helper function to format currency objects like { USD: 140 }
const formatCurrency = (currencyObj) => {
  if (!currencyObj || Object.keys(currencyObj).length === 0) return "-";
  return Object.entries(currencyObj)
    .map(([key, value]) => `${key}: ${value}`)
    .join(", ");
};

// Helper function to format a date string (date only)
const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString();
};

const SaleRow = ({ sale, onEdit, onDelete, onPrint, index, isDarkMode }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  // Render sale items as a nested table (without currency column)
  const renderItemsTable = () => {
    if (sale.sale_items && sale.sale_items.length > 0) {
      return (
        <Table
          size="small"
          aria-label="sale items"
          sx={{
            "& .MuiTableCell-root": {
              color: isDarkMode ? "#e5e7eb" : "inherit",
              borderColor: isDarkMode ? "rgba(255,255,255,0.08)" : undefined,
            },
            "& .MuiTableRow-root": {
              backgroundColor: isDarkMode ? "#1d1d1d" : "transparent",
            },
          }}
        >
          <TableHead>
            <TableRow sx={{ bgcolor: isDarkMode ? "#243524" : "#f5f5f5" }}>
              <TableCell><strong>{t("sale.product")}</strong></TableCell>
              <TableCell><strong>{t("sale.quantity")}</strong></TableCell>
              <TableCell><strong>{t("purchase.title", "Purchase")}</strong></TableCell>
              <TableCell><strong>{t("purchase.buyPrice", "Buy Price")}</strong></TableCell>
              <TableCell><strong>{t("sale.salePrice", "Sale Price")}</strong></TableCell>
              <TableCell><strong>{t("sale.unit")}</strong></TableCell>
              <TableCell><strong>Subtotal</strong></TableCell>
              <TableCell><strong>Profit</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sale.sale_items.map((item, idx) => {
              const qty = parseFloat(item.quantity) || 0;
              const salePrice = parseFloat(item.sale_price) || 0;
              const buyPrice = parseFloat(
                item.buy_price ?? item.purchase_details?.buy_price ?? item.product_details?.buy_price
              ) || 0;
              const subtotal = qty * salePrice;
              const profit = buyPrice > 0 ? subtotal - qty * buyPrice : null;
              return (
                <TableRow key={idx}>
                  <TableCell>
                    {item.product_details?.name || t("sale.unknownProduct")}
                  </TableCell>
                  <TableCell>{item.quantity}</TableCell>
                  <TableCell>
                    {item.purchase_details ? (
                      <Box>
                        <Typography variant="caption" display="block">ID: {item.purchase_details.id}</Typography>
                        <Typography variant="caption" display="block">{item.purchase_details.supplier_name}</Typography>
                      </Box>
                    ) : "-"}
                  </TableCell>
                  <TableCell sx={{ color: "#e65100" }}>
                    {buyPrice > 0 ? buyPrice.toFixed(2) : "-"}
                  </TableCell>
                  <TableCell sx={{ color: "#0d47a1", fontWeight: 600 }}>
                    {salePrice > 0 ? salePrice.toFixed(2) : "-"}
                  </TableCell>
                  <TableCell>{item.unit_details?.title || "-"}</TableCell>
                  <TableCell>{subtotal.toFixed(2)}</TableCell>
                  <TableCell>
                    {profit !== null ? (
                      <Chip
                        label={profit.toFixed(2)}
                        size="small"
                        color={profit >= 0 ? "success" : "error"}
                        variant="outlined"
                      />
                    ) : "-"}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      );
    }
    return <Typography variant="body2">{t("sale.noItems")}</Typography>;
  };

  return (
    <>
      <TableRow hover>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
            sx={{ color: isDarkMode ? "#f3f4f6" : "inherit" }}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
          {index}
        </TableCell>
        <TableCell sx={{ color: isDarkMode ? "#f3f4f6" : "inherit" }}>{sale.customer_details?.name || "-"}</TableCell>
        <TableCell sx={{ color: isDarkMode ? "#f3f4f6" : "inherit" }}>{formatDate(sale.sale_date)}</TableCell>
        <TableCell>
          {sale.amount && parseFloat(sale.amount) > 0
            ? `${sale.currency_details?.title || ""}: ${parseFloat(sale.amount).toFixed(2)}`
            : "-"}
        </TableCell>
        <TableCell sx={{ color: isDarkMode ? "#f3f4f6" : "inherit" }}>{formatCurrency(sale.effective_total_amount)}</TableCell>
        <TableCell>
          {sale.received_amount && parseFloat(sale.received_amount) > 0
            ? `${sale.currency_details?.title || ""}: ${parseFloat(sale.received_amount).toFixed(2)}`
            : "-"}
        </TableCell>
        <TableCell sx={{ color: isDarkMode ? "#f3f4f6" : "inherit" }}>{formatCurrency(sale.total_remained_amount)}</TableCell>
        <TableCell sx={{ color: isDarkMode ? "#f3f4f6" : "inherit" }}>{sale.sale_items?.length || 0}</TableCell>
        <TableCell>
          <IconButton
            onClick={() => onEdit(sale)}
            size="small"
            sx={{ mr: 1, color: isDarkMode ? "#f3f4f6" : "inherit" }}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            onClick={() => onPrint(sale)}
            size="small"
            sx={{ mr: 1 }}
            color={isDarkMode ? "success" : "primary"}
          >
            <PrintIcon />
          </IconButton>
          <IconButton
            onClick={() => onDelete(sale.id)}
            size="small"
            color="error"
          >
            <DeleteIcon />
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1, color: isDarkMode ? "#e5e7eb" : "inherit" }}>
              <Typography variant="subtitle2" gutterBottom sx={{ color: isDarkMode ? "#e5e7eb" : "inherit" }}>
                {t("sale.saleItems")}
              </Typography>
              {renderItemsTable()}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const SaleIndex = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDarkMode = theme?.palette?.mode === "dark";
  const auth = useAuth();
  const token = auth?.user?.token;
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCustomer, setFilterCustomer] = useState("");
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [selectedCustomerOption, setSelectedCustomerOption] = useState(
    ALL_CUSTOMERS_OPTION
  );
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editData, setEditData] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showReport, setShowReport] = useState(false);

  const { data: customersFilterData, isLoading: customerFilterLoading } =
    useFetchObjects(
      ["customer", "sales-filter", customerSearchTerm],
      `shop/customers/?search=${encodeURIComponent(
        customerSearchTerm.trim()
      )}&page_size=20`,
      token
    );

  const baseCustomerOptions = Array.isArray(customersFilterData)
    ? customersFilterData
    : customersFilterData?.results || [];
  const customersFilter = [
    ALL_CUSTOMERS_OPTION,
    ...(selectedCustomerOption && String(selectedCustomerOption.id) !== ""
      ? [selectedCustomerOption]
      : []),
    ...baseCustomerOptions.filter(
      (customer) =>
        String(customer.id) !== String(selectedCustomerOption?.id || "")
    ),
  ];

  // Build query URL with proper parameters
  const buildQuery = () => {
    const params = new URLSearchParams();

    if (searchTerm) {
      params.append("search", searchTerm.trim());
    }

    if (filterCustomer) {
      params.append("customer", filterCustomer);
    }

    if (startDate) {
      params.append("start_date", startDate);
    }

    if (endDate) {
      params.append("end_date", endDate);
    }

    if (rowsPerPage === "all") {
      params.append("page_size", "all");
    } else {
      params.append("page", page.toString());
      params.append("page_size", rowsPerPage.toString());
    }

    return `shop/sales/?${params.toString()}`;
  };

  // Fetch sales – DRF paginated response is expected.
  const { data, isLoading, isError, refetch } = useFetchObjects(
    ["sale", searchTerm, filterCustomer, startDate, endDate, page, rowsPerPage],
    buildQuery(),
    token
  );

  const { handleDelete, ConfirmDialog } = useDelete("shop/sales", token);

  useEffect(() => {
    refetch();
  }, [page, rowsPerPage, filterCustomer, searchTerm, startDate, endDate, refetch]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handleCustomerOptionChange = (event, newValue) => {
    if (!newValue || String(newValue.id) === "") {
      setSelectedCustomerOption(ALL_CUSTOMERS_OPTION);
      setFilterCustomer("");
      setCustomerSearchTerm("");
      setPage(1);
      return;
    }
    setSelectedCustomerOption(newValue);
    setFilterCustomer(String(newValue.id));
    setPage(1);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handlePrint = (sale) => {
    printSale(sale);
  };

  const sales = data?.results || data || [];
  const totalCount = data?.count || (Array.isArray(data) ? data.length : 0);
  const pageOffset = rowsPerPage === "all" ? 0 : (page - 1) * rowsPerPage;

  // Compute summary from loaded sales
  const reportSummary = React.useMemo(() => {
    const allSales = Array.isArray(sales) ? sales : [];
    let totalSaleAmount = 0, totalAmount = 0, totalReceived = 0, totalRemained = 0;
    allSales.forEach((s) => {
      const getVal = (v) => {
        if (!v) return 0;
        if (typeof v === "number") return v;
        if (typeof v === "object") return Object.values(v).reduce((a, b) => a + parseFloat(b || 0), 0);
        return parseFloat(v) || 0;
      };
      totalSaleAmount += getVal(s.effective_total_amount ?? s.total_amount);
      totalAmount += parseFloat(s.amount || 0);
      totalReceived += parseFloat(s.received_amount || 0);
      totalRemained += getVal(s.total_remained_amount);
    });
    return { count: allSales.length, totalSaleAmount, totalAmount, totalReceived, totalRemained };
  }, [sales]);
  const filterControlSx = {
    minWidth: { xs: "100%", md: 240 },
    "& .MuiOutlinedInput-root": {
      minHeight: 56,
      backgroundColor: isDarkMode ? "#1b1b1b" : "#fff",
      color: isDarkMode ? "#f3f4f6" : "inherit",
      "& fieldset": {
        borderColor: isDarkMode ? "rgba(255,255,255,0.14)" : undefined,
      },
      "&:hover fieldset": {
        borderColor: isDarkMode ? "rgba(255,255,255,0.24)" : undefined,
      },
    },
  };

  if (isError) {
    return <ErrorPage message={t("sale.failedToLoadSales")} />;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, background: isDarkMode ? "#121212" : "transparent", minHeight: "calc(100vh - 64px)" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          mb: 2,
          gap: 2,
        }}
      >
        <Typography variant="h4" sx={{ color: isDarkMode ? "#f5f5f5" : "inherit" }}>{t("sale.saleList")}</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant={showReport ? "contained" : "outlined"}
            color="secondary"
            startIcon={<AssessmentIcon />}
            onClick={() => setShowReport((v) => !v)}
          >
            {showReport ? "Hide Report" : "Show Report"}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpenAdd(true)}
          >
            {t("sale.addNewSale")}
          </Button>
        </Box>
      </Box>
        <Typography variant="subtitle1" color={isDarkMode ? "rgba(255,255,255,0.7)" : "textSecondary"} mb={2}>
        {t("sale.dataLoadedOn")} {new Date().toLocaleDateString()}
      </Typography>

      {/* Report Summary Panel */}
      {showReport && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: isDarkMode ? "#171717" : "#f8fbff", border: isDarkMode ? "1px solid rgba(255,255,255,0.08)" : "1px solid #d9e2ec" }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: isDarkMode ? "#f5f5f5" : "inherit" }}>
            📊 Sales Summary {startDate && endDate ? `(${startDate} → ${endDate})` : startDate ? `(from ${startDate})` : endDate ? `(until ${endDate})` : "(All Time)"}
          </Typography>
          <Grid container spacing={2}>
            {[
              { label: "Total Sales", value: reportSummary.count, color: "#0f4c81", isCount: true },
              { label: "Total Amount", value: reportSummary.totalSaleAmount.toFixed(2), color: "#1f7a4c" },
              { label: "Total Discount", value: reportSummary.totalAmount.toFixed(2), color: "#8a6d1f" },
              { label: "Total Received", value: reportSummary.totalReceived.toFixed(2), color: "#176087" },
              { label: "Total Remaining", value: reportSummary.totalRemained.toFixed(2), color: "#c62828" },
            ].map((item) => (
              <Grid item xs={6} sm={4} md={2.4} key={item.label}>
                <Box sx={{ p: 2, bgcolor: item.color, borderRadius: 2, textAlign: "center" }}>
                  <Typography variant={item.isCount ? "h4" : "h6"} sx={{ color: "white", fontWeight: "bold" }}>
                    {item.value}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "white" }}>{item.label}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="textSecondary">
            * Summary reflects the current page/filter results. Use the Comprehensive Sale Report for full analytics.
          </Typography>
        </Paper>
      )}
      {/* Search and filters */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
          mb: 2,
          alignItems: "stretch",
        }}
      >
        <TextField
          variant="outlined"
          fullWidth
          placeholder={t("sale.searchSales")}
          value={searchTerm}
          onChange={handleSearchChange}
          sx={{
            flex: 1,
            "& .MuiOutlinedInput-root": {
              minHeight: 56,
              backgroundColor: isDarkMode ? "#1b1b1b" : "#fff",
              color: isDarkMode ? "#f3f4f6" : "inherit",
              "& fieldset": {
                borderColor: isDarkMode ? "rgba(255,255,255,0.14)" : undefined,
              },
              "&:hover fieldset": {
                borderColor: isDarkMode ? "rgba(255,255,255,0.24)" : undefined,
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="Start Date"
          type="date"
          value={startDate}
          onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 160, "& .MuiOutlinedInput-root": { minHeight: 56, backgroundColor: isDarkMode ? "#1b1b1b" : "#fff", color: isDarkMode ? "#f3f4f6" : "inherit" } }}
        />
        <TextField
          label="End Date"
          type="date"
          value={endDate}
          onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 160, "& .MuiOutlinedInput-root": { minHeight: 56, backgroundColor: isDarkMode ? "#1b1b1b" : "#fff", color: isDarkMode ? "#f3f4f6" : "inherit" } }}
        />
        <Autocomplete
          sx={filterControlSx}
          options={customersFilter}
          value={selectedCustomerOption}
          onChange={handleCustomerOptionChange}
          onInputChange={(event, newInputValue, reason) => {
            if (reason === "input") {
              setCustomerSearchTerm(newInputValue);
            }
            if (reason === "clear") {
              setCustomerSearchTerm("");
            }
          }}
          loading={customerFilterLoading}
          getOptionLabel={(option) => option?.name || ""}
          isOptionEqualToValue={(option, value) =>
            String(option?.id) === String(value?.id)
          }
          noOptionsText={t("sale.noCustomersFound", "No customers found")}
          clearOnBlur={false}
          selectOnFocus
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t("sale.selectCustomer")}
              size="medium"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {customerFilterLoading ? (
                      <CircularProgress color="inherit" size={18} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        {(startDate || endDate) && (
          <Button
            variant="outlined"
            onClick={() => { setStartDate(""); setEndDate(""); setPage(1); }}
            sx={{ minWidth: 80, minHeight: 56 }}
          >
            Clear Dates
          </Button>
        )}
      </Box>
      {/* Sales Table */}
      <TableContainer
        component={Paper}
        sx={{
          mt: 2,
          background: isDarkMode ? "#1a1a1a" : "#fff",
          border: isDarkMode ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(0,0,0,0.06)",
          boxShadow: isDarkMode ? "0 12px 30px rgba(0,0,0,0.42)" : undefined,
          "& .MuiTableCell-root": {
            borderColor: isDarkMode ? "rgba(255,255,255,0.08)" : undefined,
          },
        }}
      >
        <Table
          aria-label="sale table"
          sx={{
            "& .MuiTableBody-root .MuiTableRow-root": {
              backgroundColor: isDarkMode ? "#252525" : "#fff",
            },
            "& .MuiTableBody-root .MuiTableRow-root:hover": {
              backgroundColor: isDarkMode ? "#2d2d2d" : "#fafafa",
            },
            "& .MuiTableCell-root": {
              color: isDarkMode ? "#f3f4f6" : "inherit",
              borderColor: isDarkMode ? "rgba(255,255,255,0.08)" : undefined,
            },
          }}
        >
          <TableHead>
            <TableRow sx={{ backgroundColor: "#355d31" }}>
              <TableCell sx={{ color: "#fff !important", fontWeight: 700 }}>{t("sale.id")}</TableCell>
              <TableCell sx={{ color: "#fff !important", fontWeight: 700 }}>
                {t("sale.customer")}
              </TableCell>
              <TableCell sx={{ color: "#fff !important", fontWeight: 700 }}>
                {t("sale.saleDate")}
              </TableCell>
              <TableCell sx={{ color: "#fff !important", fontWeight: 700 }}>
                {t("sale.discountAmount")}
              </TableCell>
              <TableCell sx={{ color: "#fff !important", fontWeight: 700 }}>
                {t("sale.totalAmount")}
              </TableCell>
              <TableCell sx={{ color: "#fff !important", fontWeight: 700 }}>
                {t("sale.totalPaid")}
              </TableCell>
              <TableCell sx={{ color: "#fff !important", fontWeight: 700 }}>
                {t("sale.totalRemained")}
              </TableCell>
              <TableCell sx={{ color: "#fff !important", fontWeight: 700 }}>
                {t("sale.itemCount")}
              </TableCell>
              <TableCell sx={{ color: "#fff !important", fontWeight: 700 }}>{t("sale.action")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? Array.from(new Array(5)).map((_, rowIndex) => (
                  <TableRow key={`skeleton-${rowIndex}`}>
                    {[ 
                      "ID",
                      "Customer",
                      "Sale Date",
                      "Discount",
                      "Total Amount",
                      "Total Paid",
                      "Total Remained",
                      "Item Count",
                      "Action",
                    ].map((col, colIndex) => (
                      <TableCell key={`skeleton-${rowIndex}-${colIndex}`}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : sales?.map((sale, i) => (
                  <SaleRow
                    key={sale.id}
                    sale={sale}
                    index={pageOffset + i + 1}
                    isDarkMode={isDarkMode}
                    onEdit={(data) => {
                      setEditData(data);
                      setOpenEdit(true);
                    }}
                    onPrint={handlePrint}
                    onDelete={(id) => handleDelete(id)}
                  />
                ))}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Pagination */}
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        padding="10px"
      >
        <Box display="flex" alignItems="center">
          <Typography sx={{ color: isDarkMode ? "#f3f4f6" : "inherit" }}>{t("hr.rowsPerPage", "Row Per Page")}</Typography>
          <FormControl
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  border: "none",
                },
              },
              "& .MuiSelect-select": {
                color: isDarkMode ? "#f3f4f6" : "inherit",
              },
            }}
          >
            <Select
              value={rowsPerPage}
              onChange={(e) => {
                const value = e.target.value;
                setRowsPerPage(value === 'all' ? 'all' : Number(value));
                setPage(1);
              }}
              sx={{
                color: isDarkMode ? "#f3f4f6" : "inherit",
                "& .MuiSelect-icon": {
                  color: isDarkMode ? "#f3f4f6" : "inherit",
                },
              }}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={30}>30</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value="all">All</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Pagination
          count={rowsPerPage === 'all' ? 1 : Math.ceil(totalCount / rowsPerPage)}
          page={page}
          onChange={handlePageChange}
          variant="outlined"
          color="primary"
          disabled={rowsPerPage === 'all'}
          sx={{
            "& .MuiPaginationItem-root": {
              color: isDarkMode ? "#f3f4f6" : "inherit",
              borderColor: isDarkMode ? "rgba(255,255,255,0.12)" : undefined,
            },
            "& .Mui-selected": {
              backgroundColor: isDarkMode ? "#355d31 !important" : undefined,
            },
          }}
        />
      </Grid>

      {openAdd && (
        <SaleAdd
          open={openAdd}
          onClose={() => setOpenAdd(false)}
          token={token}
        />
      )}
      {openEdit && (
        <SaleEdit
          open={openEdit}
          onClose={() => setOpenEdit(false)}
          editData={editData}
          token={token}
        />
      )}
      <ConfirmDialog />
    </Box>
  );
};

export default SaleIndex;
